import {Link, useLocation} from "wouter";
import {
    FiChevronDown,
    FiChevronUp,
    FiCreditCard,
    FiEdit3,
    FiHome,
    FiImage,
    FiInfo,
    FiList,
    FiLogOut,
    FiSearch,
    FiSettings
} from "react-icons/fi";
import {clsx} from "clsx";
import React from "react";
import useSWR from "swr";
import {MeResponseItem, WorkspaceResponseItem} from "@/@types/AssetTypes.ts";
import {getHttpClient, setAccessToken, swrFetcher} from "@/utils/api.ts";
import Avatar from "@/components/Avatar.tsx";
import Drawer from "@/components/Drawer.tsx";
import {closeDrawer, openDrawer} from "@/components/dispatch_functions.ts";
import {GiHamburgerMenu} from "react-icons/gi";
import {useInvalidateAllQueries} from "@/components/hooks/useInvalidateAllQueries.ts";

export default function SidebarLayout({children}) {

    return (
        <div
            className="relative flex min-h-svh w-full bg-white max-lg:flex-col dark:bg-base-300 bg-base-300">
            {/* Sidebar on desktop */}
            <div className="fixed inset-y-0 left-0 w-64 max-lg:hidden flex flex-col">
                <Sidebar />
            </div>
            {/* Sidebar on mobile */}

            <Drawer position={"start"} id={"sidebar"} title={""}>
                <Sidebar />
            </Drawer>

            {/* Navbar on mobile */}
            <header className="flex items-center px-4 lg:hidden">
                <div className="py-2.5 w-full space-between">
                    <img src={"/mask-icon.svg"} alt={"logo"} className={"h-12"}/>
                    <button onClick={()=>openDrawer('sidebar')} className={"btn btn-lg btn-ghost"}>
                        <GiHamburgerMenu className={"w-6 h-6 opacity-75"} style={{strokeWidth:1}}  />
                    </button>
                </div>
            </header>

            {/* Content */}
            <main className="flex flex-1 flex-col pb-2 lg:min-w-0 lg:pt-2 lg:pr-2 lg:pl-64">
                <div
                    className="grow p-6 lg:rounded-lg lg:bg-white lg:p-10 lg:ring-1 lg:shadow-xs  lg:ring-zinc-950/5 dark:lg:bg-zinc-900 dark:lg:ring-white/10">
                    <div className="mx-auto max-w-6xl">{children}</div>
                </div>
            </main>
        </div>
    )
}


function Sidebar(){
    const {data: workspaceData, isLoading} = useSWR<{workspaces: WorkspaceResponseItem[]}>('/me/workspaces', swrFetcher)
    const {data: me, isLoading: loadingMe} = useSWR<{user:MeResponseItem}>('/me', swrFetcher);
    const workspaces = workspaceData?.workspaces || [];
    const current = workspaces?.find((i)=> i.is_current);
    const otherWorkspaces = workspaces?.filter((i)=> !i.is_current) || []
    const invalidateAllQueries = useInvalidateAllQueries()
    const switchWorkspace = (id)=>{
        getHttpClient().post(`/auth/with_workspace`,{
            workspace_id: id
        }).then(({data})=>{
            if(data.access_token){
                setAccessToken(data.access_token)
                invalidateAllQueries()
            }
        })
    }
    return <React.Fragment>
        <div className="mt-4"/>
        <div className="mx-1">
            <div className="dropdown dropdown-center w-full">
                <button tabIndex={0} role="button"
                        className="py-3 w-full px-2 rounded-lg border border-base-300 hover:bg-base-200 flex items-center">
                    <Avatar alt={current?.workspace?.name?.substring(0, 1) || ' '}
                            src={current?.workspace?.picture?.url}
                            size={36}/>
                    <div className={"flex-1 line-clamp-1"}>
                        {current?.workspace?.name}
                    </div>
                    <FiChevronDown size={20}/>
                </button>
                <ul tabIndex={0} className="dropdown-content bg-base-100 rounded-box z-[1] w-52 p-1 shadow">
                    {
                        otherWorkspaces.map((i) => (
                            <li onClick={()=>switchWorkspace(i.id)} key={i.id} className={"cursor-pointer items-center rounded-xl px-1 hover:bg-base-200 flex flex-row py-2 gap-2"}>
                                <Avatar alt={i?.workspace?.name?.substring(0, 1) || ' '}
                                        src={i?.workspace?.picture?.url}
                                        size={36}/>
                                <span className={"line-clamp-1"}>
                                    {i?.workspace?.name}
                                </span>
                            </li>
                        ))
                    }
                    <button className={"btn btn-ghost w-full"}>+ New Workspace</button>
                </ul>
            </div>
        </div>
        <div className={"border-base-200 border mt-4"}></div>
        <div className="p-4 flex-1">
            <NavLink href="/" title="Home" icon={<FiHome size={20}/>}/>
            <NavLink href="/posts" title="Posts" icon={<FiList size={20}/>}/>
            <NavLink href="/assets" title="Assets" icon={<FiImage size={20}/>}/>
            <NavLink href="/discover" title="Discover" icon={<FiSearch size={20}/>}/>

            <NavLink href="/editor" title="Editor" icon={<FiEdit3 size={20}/>}/>
            <NavLink href="/settings" title="Settings" icon={<FiSettings size={20}/>}/>
        </div>

        <div className={"py-2 px-1"}>
            <div className="px-4">
                {/*<NavLink href="/upgrade" title="Upgrade" icon={<FiCreditCard size={20}/>}/>*/}
                <NavLink href="/support" title="Support" icon={<FiInfo size={20}/>}/>
            </div>
            <div className="dropdown dropdown-top w-full">
                <button tabIndex={0} role="button" className="py-3 px-2 w-full rounded-lg border border-base-300
                gap-2
                hover:bg-base-200 flex items-center">
                    <Avatar alt={(me?.user?.profile?.name || me?.user.email_or_phone)?.substring(0, 1) || ' '} src={me?.user?.profile?.picture?.url}
                            size={42} square />
                    <div className={"flex-1 line-clamp-2 text-start"}>
                        {me?.user?.profile?.name}
                        <br/>
                        <span className={"text-xs font-normal"}>
                            {me?.user?.email_or_phone}
                        </span>
                    </div>
                    <FiChevronUp/>
                </button>
                <ul tabIndex={0}
                    className="dropdown-content bg-base-100 border border-base-200 rounded-box z-[1] overflow-hidden w-52 shadow">
                    <DropDownLink title={"Settings"} href={"/settings"} icon={<FiSettings/>}/>
                    <DropDownLink title={"Privacy policy"} href={"/legal/privacy"} icon={<FiInfo/>}/>
                    {/*<DropDownLink title={"Share feedback"} href={"/contact/feedback"} icon={<FiMessageSquare/>}/>*/}
                    <DropDownLink title={"Sign out"} href={"/logout"} icon={<FiLogOut/>}/>
                </ul>
            </div>
        </div>
    </React.Fragment>
}

function DropDownLink({title, href, icon}) {
    return <Link href={href}
                 className={"tracking-tight px-3 py-3 hover:text-primary flex items-center gap-2"}>{icon} {title}</Link>

}

function NavLink({href, icon, title}) {
    const [location] = useLocation();
    const [, navigate] = useLocation()
    return <a className={
        clsx('flex items-center tracking-tight gap-4 py-4 duration-100 hover:translate-x-2 hover:scale-105 hover:text-primary ' +
            '', {
            'text-primary': href === location
        })}
              href={href}
                 onClick={(e)=>{
                     if(e){
                         e.preventDefault();
                     }
                     closeDrawer('sidebar')
                     navigate(href)
                 }}>
        {icon} <span>{title}</span></a>

}
